import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime, money } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { InvoiceDoc, type Invoice } from '../components/InvoiceDoc';
import { PaperWidthPicker, ThermalPrintButton } from '../components/ThermalPrint';
import { invoiceReceipt } from '../lib/receipt';
import {
  Empty,
  ErrorState,
  Loading,
  Modal,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface InvoiceRow {
  id: string;
  number: string;
  kind: 'FNB' | 'ROOM';
  status: string;
  guestName: string;
  ticketNumber: string | null;
  orderType: string | null;
  subtotal: number;
  tax: number;
  total: number;
  mode: string;
  issuedAt: string;
}

export function InvoicesPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [kind, setKind] = useState<'' | 'FNB' | 'ROOM'>('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  const invoices = useQuery({
    queryKey: ['invoices', kind, search],
    queryFn: () => api.get<InvoiceRow[]>(`/api/invoices${qs({ kind, q: search, limit: 200 })}`),
  });

  const invoice = useQuery({
    queryKey: ['invoice', openId],
    queryFn: () => api.get<Invoice>(`/api/invoices/${openId}`),
    enabled: Boolean(openId),
  });

  const voidInvoice = useMutation({
    mutationFn: () => api.post(`/api/invoices/${openId}/void`, { reason }),
    onSuccess: () => {
      push('Invoice voided and stock restored', 'ok');
      setVoiding(false);
      setReason('');
      setOpenId(null);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: fail,
  });

  if (invoices.isLoading) return <Loading label="Loading the register…" />;
  if (invoices.error)
    return <ErrorState error={invoices.error} onRetry={() => void invoices.refetch()} />;

  const rows = invoices.data ?? [];
  const settled = rows.filter((row) => row.status !== 'VOID');
  const total = settled.reduce((sum, row) => sum + row.total, 0);

  return (
    <div className="stack">
      <PageHeader title="Invoices" subtitle="Every settled bill, restaurant and rooms" />

      <div className="card">
        <div className="card-head">
          <div className="seg">
            {[
              { id: '', label: 'All' },
              { id: 'FNB', label: 'Restaurant' },
              { id: 'ROOM', label: 'Rooms' },
            ].map((option) => (
              <button
                key={option.id}
                className={option.id === kind ? 'active' : ''}
                onClick={() => setKind(option.id as '' | 'FNB' | 'ROOM')}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="search" style={{ width: 240 }}>
            <input
              className="input"
              placeholder="Invoice number or guest"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {rows.length === 0 ? (
          <Empty icon="❑" message="No invoices match" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Issued</th>
                  <th>Guest</th>
                  <th>Source</th>
                  <th className="num">Taxable</th>
                  <th className="num">GST</th>
                  <th className="num">Total</th>
                  <th>Mode</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="clickable" onClick={() => setOpenId(row.id)}>
                    <td className="b mono">{row.number}</td>
                    <td className="nowrap">{dateTime(row.issuedAt)}</td>
                    <td>{row.guestName}</td>
                    <td>
                      {row.kind === 'ROOM' ? 'Room folio' : (row.ticketNumber ?? 'Restaurant')}
                    </td>
                    <td className="num">{money(row.subtotal)}</td>
                    <td className="num dim">{money(row.tax)}</td>
                    <td className="num b">{money(row.total)}</td>
                    <td>{row.mode}</td>
                    <td>
                      <StatusChip status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}>{settled.length} settled invoice(s)</td>
                  <td className="num">{money(total)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {openId ? (
        <Modal
          title={invoice.data ? `Invoice ${invoice.data.number}` : 'Invoice'}
          size="wide"
          onClose={() => setOpenId(null)}
          footer={
            <>
              <PaperWidthPicker />
              <button className="btn" onClick={() => setOpenId(null)}>
                Close
              </button>
              {can('invoice:void') &&
              invoice.data?.status !== 'VOID' &&
              invoice.data?.kind === 'FNB' ? (
                <button className="btn danger" onClick={() => setVoiding(true)}>
                  Void invoice
                </button>
              ) : null}
              <button className="btn" onClick={() => window.print()}>
                A4 invoice
              </button>
              {invoice.data ? (
                <ThermalPrintButton
                  data={invoiceReceipt(invoice.data)}
                  className="btn primary"
                  label="Print receipt"
                />
              ) : null}
            </>
          }
        >
          {invoice.isLoading || !invoice.data ? <Loading /> : <InvoiceDoc invoice={invoice.data} />}
        </Modal>
      ) : null}

      {voiding ? (
        <Modal
          title="Void invoice"
          size="narrow"
          onClose={() => setVoiding(false)}
          footer={
            <>
              <button className="btn" onClick={() => setVoiding(false)}>
                Cancel
              </button>
              <button
                className="btn danger"
                disabled={!reason.trim() || voidInvoice.isPending}
                onClick={() => voidInvoice.mutate()}
              >
                Void and restore stock
              </button>
            </>
          }
        >
          <p className="dim" style={{ marginBottom: 14 }}>
            The invoice is cancelled, the stock it consumed is written back, and both are recorded
            in the audit log. The invoice number is never reused.
          </p>
          <TextInput
            label="Reason"
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Modal>
      ) : null}
    </div>
  );
}
